import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LoginIcon from '@mui/icons-material/Login';
import StarIcon from '@mui/icons-material/Star';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  Chip
} from '@mui/material';
import * as React from 'react';

interface BrandRevealPromptProps {
  open: boolean;
  onClose: () => void;
  onSignIn: () => void;
  brandName?: string;
  serviceCount?: number;
  showExclusiveOffers?: boolean;
}

export function BrandRevealPrompt({
  open,
  onClose,
  onSignIn,
  brandName = 'Premium Service Provider',
  serviceCount = 1,
  showExclusiveOffers = true
}: BrandRevealPromptProps) {
  const handleSignIn = () => {
    onSignIn();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <LockIcon color="primary" />
            <Typography variant="h6" component="div">
              Unlock Premium Services
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
            {brandName}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Sign in to access exclusive services and special offers from {brandName}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: 'primary.50',
            border: '1px solid',
            borderColor: 'primary.200'
          }}
        >
          <Typography variant="h6" color="primary" gutterBottom>
            What you'll unlock:
          </Typography>

          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>{serviceCount} premium service{serviceCount !== 1 ? 's' : ''}</strong> from {brandName}
              </Typography>
            </Box>
            {showExclusiveOffers && (
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Exclusive offers and discounts</strong> available only to registered users
                </Typography>
              </Box>
            )}
            <Box component="li" sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>Direct access</strong> to {brandName} support and account management
              </Typography>
            </Box>
            <Box component="li">
              <Typography variant="body2">
                <strong>Streamlined ordering</strong> with saved preferences and faster checkout
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box textAlign="center">
          <Typography variant="body2" color="text.secondary" mb={2}>
            Already have an account? Sign in to continue.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            New to TechAdds? Create your account in seconds.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth
          sx={{ py: 1.5 }}
        >
          Browse Other Services
        </Button>
        <Button
          onClick={handleSignIn}
          variant="contained"
          fullWidth
          startIcon={<LoginIcon />}
          sx={{ py: 1.5 }}
        >
          Sign In to Unlock
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Inline version for use within service cards or other components
interface InlineBrandRevealPromptProps {
  onSignIn: () => void;
  brandName?: string;
  compact?: boolean;
}

export function InlineBrandRevealPrompt({
  onSignIn,
  brandName = 'Premium Service Provider',
  compact = false
}: InlineBrandRevealPromptProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1 : 2,
        backgroundColor: 'primary.50',
        border: '1px solid',
        borderColor: 'primary.200',
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'primary.100'
        }
      }}
      onClick={onSignIn}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <LockIcon sx={{ color: 'primary.main', fontSize: compact ? 16 : 20 }} />
        <Box flex={1}>
          <Typography
            variant={compact ? "body2" : "body1"}
            color="primary"
            fontWeight="medium"
          >
            Sign in to see {brandName} services
          </Typography>
          {!compact && (
            <Typography variant="body2" color="text.secondary">
              Unlock exclusive offers and premium support
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size={compact ? "small" : "medium"}
          startIcon={<LoginIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onSignIn();
          }}
          sx={{ minWidth: 'auto' }}
        >
          Sign In
        </Button>
      </Box>
    </Paper>
  );
}

// Chip version for compact displays
interface BrandRevealChipProps {
  onSignIn: () => void;
  brandName?: string;
}

export function BrandRevealChip({ onSignIn, brandName }: BrandRevealChipProps) {
  return (
    <Chip
      icon={<LockIcon />}
      label={`Unlock ${brandName || 'Premium'} Services`}
      onClick={onSignIn}
      color="primary"
      variant="outlined"
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'primary.50'
        }
      }}
    />
  );
}